import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Label } from '@/components/ui/Label';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';

const profileSchema = z.object({
  nome: z.string().trim().min(3, 'Informe um nome com pelo menos 3 caracteres'),
  telefone: z.string()
    .min(1, 'Telefone é obrigatório')
    .refine((value) => /^\D*(\d\D*){10,11}$/.test(value), 'Telefone deve ter 10 ou 11 dígitos'),
  cargo: z.string().trim().min(1, 'Cargo é obrigatório').max(100, 'Cargo deve ter no máximo 100 caracteres'),
});

const passwordSchema = z.object({
  senhaAtual: z.string().min(1, 'A senha atual é obrigatória'),
  novaSenha: z.string().min(6, 'A nova senha deve ter pelo menos 6 caracteres'),
  confirmarSenha: z.string().min(1, 'Confirme a nova senha'),
}).refine((data) => data.novaSenha === data.confirmarSenha, {
  message: 'A confirmação de senha não confere',
  path: ['confirmarSenha'],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export const PerfilPage: React.FC = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfileForm,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      nome: user?.nome || '',
      telefone: user?.telefone || '',
      cargo: user?.cargo || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      senhaAtual: '',
      novaSenha: '',
      confirmarSenha: '',
    },
  });

  useEffect(() => {
    resetProfileForm({ nome: user?.nome || '', telefone: user?.telefone || '', cargo: user?.cargo || '' });
  }, [user?.nome, user?.telefone, user?.cargo, resetProfileForm]);

  const onSubmitProfile = async (data: ProfileFormData) => {
    try {
      setIsSavingProfile(true);
      await updateProfile({ nome: data.nome, telefone: data.telefone.trim(), cargo: data.cargo.trim() });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const onSubmitPassword = async (data: PasswordFormData) => {
    try {
      setIsSavingPassword(true);
      await changePassword({
        senhaAtual: data.senhaAtual,
        novaSenha: data.novaSenha,
      });
      resetPasswordForm();
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Meu perfil</h1>
        <p className="mt-1 text-sm text-gray-600">Atualize seus dados pessoais e credenciais de acesso.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados pessoais</CardTitle>
          <CardDescription>Altere seus dados de contato exibidos no sistema.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmitProfile(onSubmitProfile)} className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome</Label>
              <Input
                id="nome"
                type="text"
                placeholder="Seu nome completo"
                {...registerProfile('nome')}
                className={profileErrors.nome ? 'border-error-500' : ''}
              />
              {profileErrors.nome && <p className="mt-1 text-sm text-error-600">{profileErrors.nome.message}</p>}
            </div>
            <div>
              <Label htmlFor="telefone">Telefone</Label>
              <Input
                id="telefone"
                type="tel"
                placeholder="(11) 99999-9999"
                maxLength={20}
                {...registerProfile('telefone')}
                className={profileErrors.telefone ? 'border-error-500' : ''}
              />
              {profileErrors.telefone && <p className="mt-1 text-sm text-error-600">{profileErrors.telefone.message}</p>}
            </div>
            <div>
              <Label htmlFor="cargo">Cargo</Label>
              <Input
                id="cargo"
                type="text"
                placeholder="Ex.: Diretor, coordenador, secretário"
                maxLength={100}
                {...registerProfile('cargo')}
                className={profileErrors.cargo ? 'border-error-500' : ''}
              />
              {profileErrors.cargo && <p className="mt-1 text-sm text-error-600">{profileErrors.cargo.message}</p>}
            </div>
            <Button type="submit" disabled={isSavingProfile}>
              {isSavingProfile ? 'Salvando...' : 'Salvar dados'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alterar senha</CardTitle>
          <CardDescription>Use sua senha atual para definir uma nova senha.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-4">
            <div>
              <Label htmlFor="senhaAtual">Senha atual</Label>
              <Input
                id="senhaAtual"
                type="password"
                placeholder="Digite sua senha atual"
                {...registerPassword('senhaAtual')}
                className={passwordErrors.senhaAtual ? 'border-error-500' : ''}
              />
              {passwordErrors.senhaAtual && <p className="mt-1 text-sm text-error-600">{passwordErrors.senhaAtual.message}</p>}
            </div>

            <div>
              <Label htmlFor="novaSenha">Nova senha</Label>
              <Input
                id="novaSenha"
                type="password"
                placeholder="Digite sua nova senha"
                {...registerPassword('novaSenha')}
                className={passwordErrors.novaSenha ? 'border-error-500' : ''}
              />
              {passwordErrors.novaSenha && <p className="mt-1 text-sm text-error-600">{passwordErrors.novaSenha.message}</p>}
            </div>

            <div>
              <Label htmlFor="confirmarSenha">Confirmar nova senha</Label>
              <Input
                id="confirmarSenha"
                type="password"
                placeholder="Confirme sua nova senha"
                {...registerPassword('confirmarSenha')}
                className={passwordErrors.confirmarSenha ? 'border-error-500' : ''}
              />
              {passwordErrors.confirmarSenha && <p className="mt-1 text-sm text-error-600">{passwordErrors.confirmarSenha.message}</p>}
            </div>

            <Button type="submit" disabled={isSavingPassword}>
              {isSavingPassword ? 'Alterando senha...' : 'Alterar senha'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
